// ComposerAttachments — the Attach control in the email composer.
//
// CONSUMER: internal-ui.
//
// Nate Swartz, 2026-08-20: _"Once the email is open, I want the ability
// to attach documents from my computer, onedrive, sharepoint, or even
// from the current transaction."_ All four stores already existed in
// the OS; the compose window had no door onto any of them, so emailing
// a signed contract meant leaving the OS, downloading the file, and
// coming back through Outlook.
//
// Four sources, in the order they get used:
//
//   This record   — the documents on the transaction / opportunity the
//                   compose was opened from. First, because the file
//                   you want is nearly always the one on the thing
//                   you're emailing about. Absent when the composer
//                   was opened with no record (a bare "New email").
//   My computer   — a plain file picker. The only source whose bytes
//                   the browser has to carry.
//   OneDrive      — the advisor's own drive, through the existing
//                   browser in "pick" mode. Nothing is copied into
//                   SharePoint just because someone emailed it.
//   SharePoint    — the OS document library, through the existing
//                   document search.
//
// SIZE (rewritten 2026-08-25). One email carries 20 MB of files. The
// running total is on screen and the offending file is named BEFORE
// the advisor writes the email — a limit you only discover at Send is
// a limit that wastes someone's afternoon. The server enforces the same
// number independently (shared/email/outbound-attachments.ts); this is
// the explanation, not the enforcement.
//
// [History: the limit used to be 2.75 MB and the copy blamed Outlook
// for it. Neither was true — 2.75 MB is what fits in the one-request
// Graph send flow, and Outlook web uses a different flow for big mail.
// Nate Swartz found it with a 9.2 MB inspection report: _"It states
// this is a outlook limit, but the file easily uploaded in Outlook web
// platform."_]
//
// HOW A BIG FILE GETS THERE. Small computer files ride inside the send
// request as base64, same as always. A file that would push that
// inline pile past what one request can hold goes up on its own first
// (streamed, with a progress bar on its chip) and travels as a
// reference — because base64 in a JSON body costs roughly four times
// the file in memory, on both ends. Nothing about this is visible to
// the advisor: they pick a file, it attaches.

import { useCallback, useEffect, useRef, useState } from "react";
import { Cloud, FolderOpen, Laptop, Paperclip, Search, X } from "lucide-react";

import {
  api,
  apiV11,
  ApiError,
  type DocumentRec,
  type OneDriveItem,
} from "../../api";
// The leaf limits module, NOT outbound-attachments.ts — that one pulls
// in the Graph clients, which are Worker-typed and don't belong in the
// browser's tsconfig program.
import {
  EMAIL_ATTACHMENT_SINGLE_REQUEST_BYTES,
  EMAIL_ATTACHMENT_TOTAL_BYTES,
  humanBytes,
} from "../../../../shared/email/attachment-limits";
import {
  inlineAttachmentBytes,
  totalAttachmentBytes,
  type PendingAttachment,
} from "./composer-attachment-helpers";
import { Modal } from "../Modal";
import { OneDriveBrowserModal } from "../OneDriveBrowserModal";
import { Button, ErrorBanner } from "../../ui";
import type { EmailComposerRecordContext } from "./EmailComposer";

// Read a picked file as base64 without the data-URL prefix.
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read_failed"));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

// Re-exported so the composer imports one module.
export {
  splitAttachments,
  totalAttachmentBytes,
  inlineAttachmentBytes,
  type PendingAttachment,
} from "./composer-attachment-helpers";

type OpenPicker = null | "record" | "onedrive" | "sharepoint";

export function ComposerAttachments({
  value,
  onChange,
  recordContext,
  disabled,
}: {
  value: PendingAttachment[];
  onChange: (next: PendingAttachment[]) => void;
  recordContext?: EmailComposerRecordContext | null | undefined;
  disabled: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [picker, setPicker] = useState<OpenPicker>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const used = totalAttachmentBytes(value);
  const remaining = Math.max(0, EMAIL_ATTACHMENT_TOTAL_BYTES - used);

  // An upload finishes after the render that started it, so the
  // callbacks below can't close over `value` — they'd overwrite chips
  // added while the file was in flight. They read the live list from a
  // ref instead and hand a whole new one back.
  //
  // The ref is written by `commit`, not only on render: the first
  // progress event can land in the SAME tick as the onChange that added
  // the chips, before React has re-rendered and refreshed the ref. Left
  // to the render assignment alone, that first patch would be computed
  // against the pre-add list and silently delete every chip the advisor
  // just added.
  const valueRef = useRef(value);
  valueRef.current = value;
  const commit = useCallback(
    (next: PendingAttachment[]) => {
      valueRef.current = next;
      onChange(next);
    },
    [onChange],
  );
  const patchChip = useCallback(
    (key: string, patch: Partial<PendingAttachment>) => {
      commit(
        valueRef.current.map((a) => (a.key === key ? { ...a, ...patch } : a)),
      );
    },
    [commit],
  );
  const dropChip = useCallback(
    (key: string) => {
      commit(valueRef.current.filter((a) => a.key !== key));
    },
    [commit],
  );

  // Close the menu on an outside click or Escape — a menu you can only
  // dismiss by choosing something is a trap.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const add = useCallback(
    (next: PendingAttachment): boolean => {
      const wouldBe = used + next.bytes;
      if (wouldBe > EMAIL_ATTACHMENT_TOTAL_BYTES) {
        setError(
          // Never blame Outlook: 20 MB is what one email carries here,
          // and pointing at Microsoft for it is what sent Nate looking
          // for a bug that wasn't his to find.
          `“${next.name}” (${humanBytes(next.bytes)}) puts this email over the ${humanBytes(
            EMAIL_ATTACHMENT_TOTAL_BYTES,
          )} limit for one email. ${
            value.length > 0
              ? "Remove another file, or send it separately."
              : "Send it as a link from the record instead."
          }`,
        );
        return false;
      }
      setError(null);
      commit([...value, next]);
      return true;
    },
    [used, value, commit],
  );

  const remove = useCallback(
    (key: string) => {
      setError(null);
      const gone = value.find((a) => a.key === key);
      // Let go of the uploaded copy too. Best-effort: an unclaimed
      // staged file is swept within a day regardless, so a failure here
      // is not the advisor's problem.
      if (gone?.source === "staged" && gone.refId) {
        void api.deleteStagedEmailAttachment(gone.refId);
      }
      commit(value.filter((a) => a.key !== key));
    },
    [value, commit],
  );

  const onFilesPicked = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      // Accumulate locally: each addition has to see the ones before
      // it, and `value` doesn't change until React re-renders.
      let runningTotal = used;
      let runningInline = inlineAttachmentBytes(value);
      const additions: PendingAttachment[] = [];
      const toUpload: Array<{ file: File; key: string }> = [];
      // Whether this pick already has something to say. Picking three
      // files where the third is over the limit must attach the first
      // two AND keep the message about the third — clearing it because
      // "something was added" is how the advisor sends without noticing
      // a file was dropped.
      let refused = false;

      for (const [index, file] of Array.from(files).entries()) {
        if (runningTotal + file.size > EMAIL_ATTACHMENT_TOTAL_BYTES) {
          refused = true;
          setError(
            `“${file.name}” (${humanBytes(file.size)}) puts this email over the ${humanBytes(
              EMAIL_ATTACHMENT_TOTAL_BYTES,
            )} limit for one email. Remove another file, or send it separately.`,
          );
          break;
        }
        const key = `computer:${file.name}:${file.size}:${index}:${runningTotal}`;

        // INLINE OR UPLOAD. Everything that fits in the send request
        // rides along in it — one round trip, no waiting. Past that
        // point the request itself becomes the problem (base64 in JSON
        // costs ~4× the file in memory on both ends), so the file goes
        // up on its own and travels as a reference.
        const fitsInline =
          runningInline + file.size <= EMAIL_ATTACHMENT_SINGLE_REQUEST_BYTES;

        if (fitsInline) {
          try {
            const contentBase64 = await fileToBase64(file);
            additions.push({
              key,
              name: file.name,
              bytes: file.size,
              contentType: file.type || "application/octet-stream",
              source: "computer",
              contentBase64,
            });
            runningInline += file.size;
          } catch {
            refused = true;
            setError(`Couldn't read “${file.name}”.`);
            break;
          }
        } else {
          additions.push({
            key,
            name: file.name,
            bytes: file.size,
            contentType: file.type || "application/octet-stream",
            source: "staged",
            uploading: true,
            progress: 0,
          });
          toUpload.push({ file, key });
        }
        runningTotal += file.size;
      }

      if (additions.length === 0) return;
      if (!refused) setError(null);
      commit([...value, ...additions]);

      // Uploads run after the chips are on screen, so the advisor can
      // start writing the email while a 15 MB report goes up.
      for (const { file, key } of toUpload) {
        try {
          const staged = await api.stageEmailAttachment(file, (percent) =>
            patchChip(key, { progress: percent }),
          );
          patchChip(key, {
            uploading: false,
            progress: 100,
            refId: staged.id,
            bytes: staged.bytes,
            contentType: staged.content_type,
          });
        } catch (err) {
          // Drop the chip rather than leave a dead one: an attachment
          // the advisor can see but the send would skip is the exact
          // failure this whole path exists to prevent.
          dropChip(key);
          setError(
            err instanceof ApiError
              ? err.message
              : `“${file.name}” didn't finish uploading. Try attaching it again.`,
          );
        }
      }
    },
    [used, value, commit, patchChip, dropChip],
  );

  return (
    <div className="composer-attachments">
      <div className="composer-attachments-bar">
        <div className="composer-attach-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="composer-attach-trigger"
            onClick={() => setMenuOpen((o) => !o)}
            disabled={disabled}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Paperclip size={14} aria-hidden />
            Attach
          </button>
          {menuOpen ? (
            <div className="composer-attach-menu" role="menu">
              {recordContext ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setPicker("record");
                  }}
                >
                  <FolderOpen size={14} aria-hidden />
                  <span>
                    From {recordContext.label}
                    <small>Documents on this record</small>
                  </span>
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  fileInputRef.current?.click();
                }}
              >
                <Laptop size={14} aria-hidden />
                <span>
                  My computer
                  <small>Pick a file from this device</small>
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setPicker("onedrive");
                }}
              >
                <Cloud size={14} aria-hidden />
                <span>
                  OneDrive
                  <small>Your personal OneDrive</small>
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setPicker("sharepoint");
                }}
              >
                <Search size={14} aria-hidden />
                <span>
                  Search SharePoint
                  <small>Any Stewardship document</small>
                </span>
              </button>
            </div>
          ) : null}
        </div>
        {value.length > 0 ? (
          <span className="composer-attachments-total">
            {humanBytes(used)} of {humanBytes(EMAIL_ATTACHMENT_TOTAL_BYTES)}
          </span>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          void onFilesPicked(e.target.files);
          // Reset so re-picking the same file fires change again.
          e.target.value = "";
        }}
      />

      {error ? <ErrorBanner message={error} /> : null}

      {value.length > 0 ? (
        <ul className="composer-attachment-chips">
          {value.map((a) => (
            <li
              key={a.key}
              className={
                a.uploading
                  ? "composer-attachment-chip is-uploading"
                  : "composer-attachment-chip"
              }
            >
              <Paperclip size={12} aria-hidden />
              <span className="composer-attachment-chip-name" title={a.name}>
                {a.name}
              </span>
              {a.uploading ? (
                // A big file is a real wait. Saying how far along it is
                // is the difference between "working" and "broken".
                <span
                  className="composer-attachment-chip-progress"
                  role="progressbar"
                  aria-valuenow={a.progress ?? 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Uploading ${a.name}`}
                >
                  <span
                    className="composer-attachment-chip-progress-fill"
                    style={{ width: `${a.progress ?? 0}%` }}
                  />
                  <span className="composer-attachment-chip-progress-label">
                    {a.progress ?? 0}%
                  </span>
                </span>
              ) : a.bytes > 0 ? (
                <span className="composer-attachment-chip-size">
                  {humanBytes(a.bytes)}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => remove(a.key)}
                disabled={disabled}
                aria-label={`Remove ${a.name}`}
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {picker === "record" && recordContext ? (
        <RecordDocumentPicker
          recordContext={recordContext}
          onClose={() => setPicker(null)}
          onPick={(doc) => {
            const ok = add({
              key: `document:${doc.id}`,
              name: doc.filename,
              bytes: doc.file_size_bytes ?? 0,
              contentType: doc.mime_type ?? "application/octet-stream",
              source: "document",
              refId: doc.id,
            });
            if (ok) setPicker(null);
          }}
          alreadyAttachedIds={value
            .filter((a) => a.source === "document")
            .map((a) => a.refId!)}
        />
      ) : null}

      {picker === "onedrive" ? (
        <OneDriveBrowserModal
          open
          mode="pick"
          maxBytes={remaining}
          onClose={() => setPicker(null)}
          onPick={(item: OneDriveItem) => {
            add({
              key: `onedrive:${item.id}`,
              name: item.name,
              bytes: typeof item.size === "number" ? item.size : 0,
              contentType: "application/octet-stream",
              source: "onedrive",
              refId: item.id,
            });
            setPicker(null);
          }}
        />
      ) : null}

      {picker === "sharepoint" ? (
        <SharePointSearchPicker
          onClose={() => setPicker(null)}
          onPick={(hit) => {
            const ok = add({
              key: `sharepoint:${hit.id}`,
              name: hit.name,
              // The search index doesn't report size. The server checks
              // the real total when it fetches the bytes, so an
              // oversize file is refused there with the file named.
              bytes: 0,
              contentType: "application/octet-stream",
              source: "sharepoint",
              refId: hit.id,
            });
            if (ok) setPicker(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ─── This record's documents ────────────────────────────────────────

function RecordDocumentPicker({
  recordContext,
  onClose,
  onPick,
  alreadyAttachedIds,
}: {
  recordContext: EmailComposerRecordContext;
  onClose: () => void;
  onPick: (doc: DocumentRec) => void;
  alreadyAttachedIds: string[];
}) {
  const [docs, setDocs] = useState<DocumentRec[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiV11.listDocumentsForEntity(
          recordContext.entityType,
          recordContext.entityId,
        );
        // Archived documents are still returned; an archived file is
        // not something to send to a client.
        if (!cancelled) setDocs(resp.items.filter((d) => !d.archived));
      } catch (err) {
        if (!cancelled) {
          setDocs([]);
          setError(
            err instanceof ApiError
              ? err.message
              : "Couldn't load this record's documents.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recordContext.entityType, recordContext.entityId]);

  const attached = new Set(alreadyAttachedIds);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Attach from ${recordContext.label}`}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      {error ? <ErrorBanner message={error} /> : null}
      {docs === null ? (
        <p className="composer-attach-empty">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="composer-attach-empty">
          No documents on this record yet.
        </p>
      ) : (
        <ul className="composer-attach-doc-list">
          {docs.map((doc) => {
            const isAttached = attached.has(doc.id);
            return (
              <li key={doc.id}>
                <button
                  type="button"
                  onClick={() => onPick(doc)}
                  disabled={isAttached}
                >
                  <span className="composer-attach-doc-name">
                    {doc.filename}
                  </span>
                  <span className="composer-attach-doc-meta">
                    {isAttached
                      ? "Already attached"
                      : doc.file_size_bytes
                        ? humanBytes(doc.file_size_bytes)
                        : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

// ─── SharePoint document search ─────────────────────────────────────

interface DocumentHit {
  id: string;
  name: string;
  last_modified: string | null;
}

function SharePointSearchPicker({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (hit: DocumentHit) => void;
}) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<DocumentHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const resp = await api.searchDocuments(query.trim());
        if (!cancelled) setHits(resp.documents);
      } catch (err) {
        if (!cancelled) {
          setHits([]);
          setError(
            err instanceof ApiError ? err.message : "Document search failed.",
          );
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Attach from SharePoint"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <div className="composer-attach-search">
        <label htmlFor="composer-attach-sp-search">
          Search Stewardship documents
        </label>
        <input
          id="composer-attach-sp-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="PSA, disclosure, Duncan…"
          autoFocus
        />
      </div>
      {error ? <ErrorBanner message={error} /> : null}
      {searching ? <p className="composer-attach-empty">Searching…</p> : null}
      {!searching && query.trim().length >= 2 && hits.length === 0 && !error ? (
        <p className="composer-attach-empty">No matches.</p>
      ) : null}
      {hits.length > 0 ? (
        <ul className="composer-attach-doc-list">
          {hits.map((hit) => (
            <li key={hit.id}>
              <button type="button" onClick={() => onPick(hit)}>
                <span className="composer-attach-doc-name">{hit.name}</span>
                <span className="composer-attach-doc-meta">
                  {hit.last_modified
                    ? new Date(hit.last_modified).toLocaleDateString()
                    : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Modal>
  );
}
